'use client';

import React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PortableTextBlock } from 'sanity';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shadcn/ui/accordion';
import { padStart } from 'lodash-es';
import { motion } from 'framer-motion';

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="flex-shrink-0 w-[2rem] h-[2rem]"
    >
      <line
        x1="8"
        y1="1"
        x2="8"
        y2="15"
        stroke="#161616"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="1"
        y1="8"
        x2="15"
        y2="8"
        stroke="#161616"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="flex-shrink-0 w-[2rem] h-[2rem]"
    >
      <line
        x1="1"
        y1="8"
        x2="15"
        y2="8"
        stroke="#161616"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function FAQListNewWebsite({
  faq,
  className = '',
}: {
  faq: {
    question: string;
    answer: PortableTextBlock[];
  }[];
  className?: string;
}) {
  if (!faq || faq.length === 0) return null;

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="item-1"
      className={cn(
        'relative flex flex-col gap-[1.25rem] lg:gap-[2rem]',
        className,
      )}
    >
      {faq.map((item, index) => (
        <motion.div
          key={index + 1}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.6,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
        >
          <AccordionItem
            value={`item-${index + 1}`}
            className={cn(
              'flex flex-col group rounded-[0.25rem] p-[3rem] border border-black/10 data-[state=open]:border-transparent data-[state=open]:bg-gradient-to-b data-[state=open]:from-black/5 data-[state=open]:to-black/[3%]',
            )}
          >
            <AccordionPrimitive.Header>
              <AccordionPrimitive.Trigger className="group/trigger flex w-full items-center gap-[3rem]  text-left outline-none">
                <p className="mb-0 flex-1 font-geist text-[2.25rem] font-normal leading-[1.24] text-black lg:leading-[1.12]">
                  {item.question}
                </p>
                <div className="flex-shrink-0">
                  <div className="group-data-[state=open]/trigger:hidden">
                    <PlusIcon />
                  </div>
                  <div className="hidden group-data-[state=open]/trigger:block">
                    <MinusIcon />
                  </div>
                </div>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>

            <AccordionContent className="pb-0">
              <div className="richtext richtext-start prose-p:font-geist prose-p:text-black/50 prose-p:text-[2rem] prose-p:leading-[1.5] lg:prose-p:text-[2.25rem] mt-[1.25rem]">
                {item?.answer && (
                  <PortableText
                    value={item.answer}
                    components={richTextComponent}
                  />
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </motion.div>
      ))}
    </Accordion>
  );
}
